/**
 * Fusion macro view — presentation structure over the case envelope.
 *
 * The backend declares WHAT things are (`phase`, `group`, cluster totals,
 * seizure targets); this module decides HOW the canvas groups them (R23/R24):
 *
 *   - Phase lanes: one compound "lane" node per declared phase, in the
 *     envelope's `phases` order. fusionLayout partitions ELK by `lane_order`,
 *     so lanes run left to right.
 *   - Group boxes: one compound node per declared (phase, group) bundle, inside
 *     its lane, headed "N wallets · per-token totals in" from the envelope's
 *     exact `groups` sums (never a cross-token total, R15).
 *   - Actions layer (opt-in): one notice node per seizure-matrix target, linked
 *     from the target node by a `subpoena` edge, in an "Actions" lane. A target
 *     with no deposit tx hash carries `evidence_missing: true`.
 *
 * Everything here is data on elements (types, ids, parents, numbers). Colours,
 * dashes and opacity come from the stylesheet in types/fusion.ts. No
 * coordinates are written; ELK owns positions.
 *
 * When the envelope carries no phases, nodes stay unparented and the existing
 * placement-role partitions apply (nothing changes for older cases).
 */
import type { ElementDefinition } from 'cytoscape';
import { ROLE_PARTITION } from './fusionLayout';
import { VISIBILITY } from '../../constants/visibility';
import {
  formatAmount, formatAmountsByToken,
  type CaseGraph, type CaseGroupSummary, type CasePhase, type SeizureTargetEnv,
} from '../../services/caseCanvas';

export const LANE_PREFIX = 'lane:';
export const GROUP_PREFIX = 'grp:';
export const ACTION_PREFIX = 'action:';
export const ACTION_EDGE_PREFIX = 'action-edge:';
export const UNPHASED_LANE_ID = `${LANE_PREFIX}~unphased`;
export const ACTION_PHASE = 'action';

export const laneId = (phase: string): string => `${LANE_PREFIX}${phase}`;
export const groupKey = (phase: string | null | undefined, group: string): string => `${phase || ''}|${group}`;
export const groupBoxId = (phase: string | null | undefined, group: string): string =>
  `${GROUP_PREFIX}${groupKey(phase, group)}`;

type Data = Record<string, unknown>;
const dataOf = (el: ElementDefinition): Data => (el.data || {}) as Data;
const isEdge = (d: Data): boolean => typeof d.source === 'string' && typeof d.target === 'string';
const str = (v: unknown): string | undefined => (typeof v === 'string' && v.trim() ? v.trim() : undefined);

function humanize(id: string): string {
  return id.replace(/_/g, ' ');
}

/** "4 wallets · 60 USDT in" — the group box header line. */
export function groupHeader(summary: CaseGroupSummary | undefined, memberCount: number): string {
  const wallets = summary?.wallet_count ?? memberCount;
  const amounts = formatAmountsByToken(summary?.amounts_in_by_token);
  const count = `${wallets} wallet${wallets === 1 ? '' : 's'}`;
  return amounts ? `${count} · ${amounts} in` : count;
}

/** Notice state as printed on the notice node and in the report. */
export function noticeStateText(t: SeizureTargetEnv): string {
  if (str(t.notice_state)) return String(t.notice_state);
  if (t.serveable === true) return t.notice_template_key ? `notice ready (${t.notice_template_key})` : 'serveable';
  if (t.serveable === false) return 'not serveable';
  return 'notice state not recorded';
}

function shortAddr(a: string): string {
  return a.length > 16 ? `${a.slice(0, 6)}…${a.slice(-6)}` : a;
}

export function noticeLabel(t: SeizureTargetEnv): string {
  const who = str(t.entity_name) || (t.address ? shortAddr(String(t.address)) : `Target ${t.index + 1}`);
  const lines = [`Notice · ${who}`, noticeStateText(t)];
  if (t.evidence_missing === true) lines.push('EVIDENCE MISSING');
  else if (typeof t.amount === 'number') lines.push(`${formatAmount(t.amount)}${t.token ? ` ${t.token}` : ''}`);
  return lines.join('\n');
}

/** Lane order: the envelope's phases, then "Not phased", then Actions. */
function laneList(
  phasesPresent: Set<string>,
  declared: CasePhase[],
  needUnphased: boolean,
  needActions: boolean,
): { id: string; phase: string | null; label: string; order: number }[] {
  const ordered: { phase: string; label: string }[] = [];
  const seen = new Set<string>();
  for (const p of [...declared].sort((a, b) => a.order - b.order)) {
    if (p.id === ACTION_PHASE || !phasesPresent.has(p.id) || seen.has(p.id)) continue;
    ordered.push({ phase: p.id, label: p.label || humanize(p.id) });
    seen.add(p.id);
  }
  // Phases on elements that the envelope did not list (defensive): alphabetical.
  for (const p of [...phasesPresent].sort()) {
    if (p === ACTION_PHASE || seen.has(p)) continue;
    ordered.push({ phase: p, label: humanize(p) });
    seen.add(p);
  }
  const lanes: { id: string; phase: string | null; label: string; order: number }[] =
    ordered.map((p, i) => ({ id: laneId(p.phase), phase: p.phase, label: p.label, order: i }));
  if (needUnphased) lanes.push({ id: UNPHASED_LANE_ID, phase: null, label: 'Not phased', order: lanes.length });
  if (needActions) {
    const declaredAction = declared.find((p) => p.id === ACTION_PHASE);
    lanes.push({ id: laneId(ACTION_PHASE), phase: ACTION_PHASE, label: declaredAction?.label || 'Actions', order: lanes.length });
  }
  return lanes;
}

export interface DecorateOptions {
  /** Show the Actions layer (seizure targets → notice nodes). */
  showActions?: boolean;
}

/**
 * Add lanes, group boxes and (optionally) the Actions layer to `elements`.
 * Pure: returns a new array; input elements are not mutated. Parents are
 * emitted before children and nodes before edges.
 */
export function decorateCaseElements(
  elements: ElementDefinition[],
  graph: Pick<CaseGraph, 'phases' | 'groups' | 'seizure_targets'> | null | undefined,
  opts: DecorateOptions = {},
): ElementDefinition[] {
  const nodes = elements.filter((el) => !isEdge(dataOf(el)));
  const edges = elements.filter((el) => isEdge(dataOf(el)));
  const nodeIds = new Set(nodes.map((el) => String(dataOf(el).id)));
  const targets = (graph?.seizure_targets || []).filter((t) => t && typeof t === 'object');
  const showActions = Boolean(opts.showActions) && targets.length > 0;

  const phasesPresent = new Set<string>();
  for (const el of nodes) {
    const ph = str(dataOf(el).phase);
    if (ph && !dataOf(el).parent) phasesPresent.add(ph);
  }
  const lanesActive = phasesPresent.size > 0;
  const hasUnphased = lanesActive && nodes.some((el) => !str(dataOf(el).phase) && !dataOf(el).parent);
  const needActionLane = showActions || phasesPresent.has(ACTION_PHASE);
  const lanes = lanesActive ? laneList(phasesPresent, graph?.phases || [], hasUnphased, needActionLane) : [];
  const laneByPhase = new Map(lanes.filter((l) => l.phase).map((l) => [l.phase as string, l]));

  const summaries = new Map((graph?.groups || []).map((g) => [g.key, g]));
  const boxes = new Map<string, { id: string; phase: string | null; group: string; members: string[] }>();
  const outNodes: ElementDefinition[] = [];

  for (const el of nodes) {
    const d = dataOf(el);
    if (d.parent) { outNodes.push(el); continue; }   // already structured (e.g. demo overlay)
    const phase = str(d.phase) || null;
    const group = str(d.group);
    let parent: string | undefined;
    if (group) {
      const key = groupKey(phase, group);
      let box = boxes.get(key);
      if (!box) {
        box = { id: groupBoxId(phase, group), phase, group, members: [] };
        boxes.set(key, box);
      }
      box.members.push(String(d.id));
      parent = box.id;
    } else if (lanesActive) {
      parent = phase ? laneByPhase.get(phase)?.id : UNPHASED_LANE_ID;
    }
    const extra: Data = {};
    if (parent) extra.parent = parent;
    if (showActions && d.seizure_target === true) extra.action_target = true;
    outNodes.push(Object.keys(extra).length ? { ...el, data: { ...d, ...extra } } : el);
  }

  const boxNodes: ElementDefinition[] = [...boxes.values()].map((box) => {
    const summary = summaries.get(groupKey(box.phase, box.group));
    const title = summary?.label || humanize(box.group);
    const memberIds = summary?.node_ids?.length ? summary.node_ids : box.members;
    const lane = box.phase ? laneByPhase.get(box.phase) : (lanesActive ? { id: UNPHASED_LANE_ID } : undefined);
    return {
      data: {
        id: box.id, type: 'group_box', module: 'crypto', step: 1,
        label: `${title}\n${groupHeader(summary, box.members.length)}`,
        group: box.group, ...(box.phase ? { phase: box.phase } : {}),
        group_key: groupKey(box.phase, box.group),
        member_ids: memberIds,
        wallet_count: summary?.wallet_count ?? box.members.length,
        ...(summary ? {
          amounts_in_by_token: summary.amounts_in_by_token,
          amounts_out_by_token: summary.amounts_out_by_token,
        } : {}),
        ...(summary?.cluster_ids?.length ? { cluster_ids: summary.cluster_ids } : {}),
        ...(lane ? { parent: lane.id } : {}),
      },
    };
  });

  const actionNodes: ElementDefinition[] = [];
  const actionEdges: ElementDefinition[] = [];
  if (showActions) {
    const actionLane = laneByPhase.get(ACTION_PHASE);
    targets.forEach((t, i) => {
      const idx = typeof t.index === 'number' ? t.index : i;
      const id = `${ACTION_PREFIX}${idx}`;
      const targetNode = t.node_id && nodeIds.has(String(t.node_id)) ? String(t.node_id) : undefined;
      actionNodes.push({
        data: {
          id, type: 'notice', module: 'shared', step: 1,
          label: noticeLabel({ ...t, index: idx }),
          phase: ACTION_PHASE, placement_role: 'context',
          notice_state: noticeStateText(t),
          ...(t.evidence_missing === true ? { evidence_missing: true } : {}),
          ...(targetNode ? { target_node_id: targetNode } : {}),
          seizure: { ...t, index: idx },
          ...(t.address ? { target_address: t.address } : {}),
          ...(t.chain ? { chain: t.chain } : {}),
          ...(actionLane ? { parent: actionLane.id } : {}),
        },
      });
      if (targetNode) {
        actionEdges.push({
          data: {
            id: `${ACTION_EDGE_PREFIX}${idx}`, source: targetNode, target: id,
            edge_type: 'subpoena', determinism: 'deterministic', value: 0, step: 1,
            _edgeLabel: 'notice',
          },
        });
      }
    });
  }

  const laneNodes: ElementDefinition[] = lanes.map((l) => ({
    data: {
      id: l.id, type: 'phase_lane', label: l.label, lane_order: l.order, step: 1,
      ...(l.phase ? { phase: l.phase } : {}),
    },
    selectable: false,
    grabbable: false,
  }));

  return [...laneNodes, ...boxNodes, ...outNodes, ...actionNodes, ...edges, ...actionEdges];
}

/** Carried selection across a level switch or a layer toggle. */
export interface CarriedSelection {
  id: string;
  /** Members of the selected cluster / group box at the old altitude. */
  members?: string[];
}

/**
 * Which node to select after a remount: the same id; else the cluster or group
 * box that now contains it; else the first member of the old cluster that is
 * present. Null when nothing maps.
 */
export function resolveCarriedSelection(nodes: Data[], carry: CarriedSelection | null): string | null {
  if (!carry) return null;
  const ids = new Set(nodes.map((d) => String(d.id)));
  if (ids.has(carry.id)) return carry.id;
  const container = (key: 'collapsed_members' | 'member_ids') => nodes.find((d) =>
    Array.isArray(d[key]) && (d[key] as unknown[]).map(String).includes(carry.id));
  const cluster = container('collapsed_members');
  if (cluster) return String(cluster.id);
  const box = container('member_ids');
  if (box) return String(box.id);
  for (const m of carry.members || []) if (ids.has(m)) return m;
  return null;
}

/** Members to expand for a selected cluster or group box (empty otherwise). */
export function expandableMembers(d: Data | null | undefined): string[] {
  if (!d) return [];
  if (Array.isArray(d.collapsed_members) && d.collapsed_members.length) return (d.collapsed_members as unknown[]).map(String);
  if (d.type === 'group_box' && Array.isArray(d.member_ids)) return (d.member_ids as unknown[]).map(String);
  return [];
}

// ── Collapsed group boxes (Evidence opens folded) ─────────────────────────────────────────────

type EdgeAgg = {
  source: string; target: string; type: string; ids: string[]; transfers: number;
  amounts: Record<string, number>; anyLive: boolean; allStale: boolean;
};

/** Per-token amounts of one canvas edge: its declared per-token totals, else its single value. */
function edgeAmounts(d: Data): Record<string, number> {
  const declared = d.amounts_by_token;
  if (declared && typeof declared === 'object') {
    const out: Record<string, number> = {};
    for (const [tok, v] of Object.entries(declared as Record<string, unknown>)) {
      if (typeof v === 'number' && Number.isFinite(v)) out[tok] = v;
    }
    if (Object.keys(out).length) return out;
  }
  const tok = str(d.token_symbol);
  const v = typeof d.value === 'number' ? d.value : 0;
  return tok && v > 0 ? { [tok]: v } : {};
}

/** Most common declared placement role among the members (ties: the column further left), else ''. */
function dominantRole(members: Data[]): string {
  const counts = new Map<string, number>();
  for (const m of members) {
    const r = str(m.placement_role);
    if (r && r in ROLE_PARTITION) counts.set(r, (counts.get(r) || 0) + 1);
  }
  let best = '';
  for (const [r, n] of counts) {
    if (!best || n > (counts.get(best) as number)
      || (n === counts.get(best) && ROLE_PARTITION[r] < ROLE_PARTITION[best])) best = r;
  }
  return best;
}

/** A group with more members than this is never drawn as nodes: one card and a paged list. */
export const BIG_GROUP_MIN = 40;

export type OriginClass = 'exchange_withdrawal' | 'own_cluster' | 'p2p' | 'unknown';
export const ORIGIN_CLASSES: { key: OriginClass; label: string }[] = [
  { key: 'exchange_withdrawal', label: 'exchange withdrawal' },
  { key: 'own_cluster', label: 'own-wallet cluster' },
  { key: 'p2p', label: 'P2P counterparty' },
  { key: 'unknown', label: 'unknown' },
];

const lower = (v: unknown): string => (typeof v === 'string' ? v.toLowerCase() : '');

/**
 * Where a wallet in an origins group came from, read from the member's own data (never guessed):
 * an exchange (type, semantic role or attributed entity type) = exchange withdrawal; a member of
 * a wallet cluster = own-wallet cluster; a P2P bridge, handle or attributed P2P merchant = P2P
 * counterparty; everything else = unknown.
 */
export function originClassOf(d: Data): OriginClass {
  const attr = (d.attribution && typeof d.attribution === 'object' ? d.attribution : {}) as Data;
  const kinds = [lower(d.type), lower(d.entity_type), lower(attr.entity_type), lower(attr.category)];
  if (kinds.some((k) => k === 'exchange' || k === 'cex') || lower(d.semantic_role) === 'cex_source') return 'exchange_withdrawal';
  if (str(d.cluster_id) || str(attr.cluster_id) || kinds.includes('cluster')) return 'own_cluster';
  if (lower(d.semantic_role) === 'p2p_bridge' || kinds.some((k) => k === 'handle' || k.startsWith('p2p'))) return 'p2p';
  return 'unknown';
}

/** "exchange withdrawal 120 · own-wallet cluster 40 · unknown 206": zero classes are left out. */
export function originLine(counts: Partial<Record<OriginClass, number>>): string {
  return ORIGIN_CLASSES.filter((c) => (counts[c.key] || 0) > 0).map((c) => `${c.label} ${counts[c.key]}`).join(' · ');
}

/** One row of the member list of a big group. */
export interface MemberRow { id: string; label: string; address: string; origin: OriginClass; risk: number }

/** Members of a group box (from the full element list), sorted by label then id, for the paged list. */
export function groupMemberRows(elements: ElementDefinition[], boxId: string): MemberRow[] {
  return elements
    .filter((el) => !isEdge(dataOf(el)) && dataOf(el).parent === boxId)
    .map((el) => {
      const d = dataOf(el);
      return {
        id: String(d.id), label: str(d.label) || String(d.id), address: str(d.address) || str(d.canonical_id) || '',
        origin: originClassOf(d), risk: Number(d.risk_score) || 0,
      };
    })
    .sort((a, b) => (a.label < b.label ? -1 : a.label > b.label ? 1 : (a.id < b.id ? -1 : a.id > b.id ? 1 : 0)));
}

/** Direct member nodes of every group box found in `nodes`, keyed by box id. */
function boxMembers(nodes: ElementDefinition[]): Map<string, ElementDefinition[]> {
  const boxIds = new Set(nodes.filter((el) => dataOf(el).type === 'group_box').map((el) => String(dataOf(el).id)));
  const direct = new Map<string, ElementDefinition[]>();
  for (const el of nodes) {
    const p = dataOf(el).parent;
    if (typeof p === 'string' && boxIds.has(p)) direct.set(p, [...(direct.get(p) || []), el]);
  }
  return direct;
}

/** Edge types that say nothing about the direction of money: ignored when ranks are derived. */
const NON_FLOW_EDGE_TYPES = new Set(['counterfeit', 'annotation_link', 'subpoena']);

/**
 * Declare a placement role for every leaf node whose producer declared none, by the report's rule
 * (forensic_flow._diagram_role): no incoming flow edge = source, no outgoing = sink, both = relay,
 * no flow edge at all = context. The case subject (identity hub) is the subject and a ghost
 * placeholder is context. Roles already declared are never changed. This declares MEANING; the
 * layout engine owns the columns (R24). Pure; input untouched.
 */
export function declarePlacementRoles(elements: ElementDefinition[]): ElementDefinition[] {
  const nodes = elements.filter((el) => !isEdge(dataOf(el)));
  const isParent = new Set<string>();
  for (const el of nodes) { const p = dataOf(el).parent; if (typeof p === 'string') isParent.add(p); }
  const leaf = (d: Data) => !isParent.has(String(d.id))
    && d.type !== 'phase_lane' && !(d.type === 'group_box' && d.collapsed !== true);
  const leafIds = new Set(nodes.map(dataOf).filter(leaf).map((d) => String(d.id)));
  const indeg = new Map<string, number>();
  const outdeg = new Map<string, number>();
  for (const el of elements) {
    const d = dataOf(el);
    if (!isEdge(d) || d.stale === true) continue;
    if (NON_FLOW_EDGE_TYPES.has(String(d.edge_type || ''))) continue;
    const s = String(d.source);
    const t = String(d.target);
    if (!leafIds.has(s) || !leafIds.has(t) || s === t) continue;
    outdeg.set(s, (outdeg.get(s) || 0) + 1);
    indeg.set(t, (indeg.get(t) || 0) + 1);
  }
  const roleOf = (d: Data): string => {
    const id = String(d.id);
    if (d.type === 'identity_hub') return 'subject';
    if (d.type === 'ghost' || id.startsWith('ghost:')) return 'context';
    const i = indeg.get(id) || 0;
    const o = outdeg.get(id) || 0;
    if (i === 0 && o > 0) return 'source';
    if (o === 0 && i > 0) return 'sink';
    if (i === 0 && o === 0) return 'context';
    return 'relay';
  };
  let changed = false;
  const out = elements.map((el) => {
    const d = dataOf(el);
    if (isEdge(d) || !leaf(d)) return el;
    const declared = str(d.placement_role)?.toLowerCase();
    if (declared && (declared in ROLE_PARTITION || declared === 'ghost')) return el;
    changed = true;
    return { ...el, data: { ...d, placement_role: roleOf(d), role_derived: true } };
  });
  return changed ? out : elements;
}

/**
 * What it takes to get the nodes named by `ids` onto the canvas: the small boxes to open, and the
 * members of big groups to pin out of their card (a big group never opens into nodes).
 */
export function resolveReveal(elements: ElementDefinition[], ids: Iterable<string>): { open: string[]; pin: string[] } {
  const wanted = new Set([...ids].map(String));
  const open: string[] = [];
  const pin: string[] = [];
  for (const box of groupBoxesHolding(elements, wanted)) {
    const rows = groupMemberRows(elements, box);
    if (rows.length > BIG_GROUP_MIN) {
      rows.forEach((r) => { if (wanted.has(r.id) || (r.address && wanted.has(r.address))) pin.push(r.id); });
    } else {
      open.push(box);
    }
  }
  return { open, pin };
}

/** Ids of the group boxes whose members match any of `ids` (node id, canonical id, address or entity id). */
export function groupBoxesHolding(elements: ElementDefinition[], ids: Iterable<string>): string[] {
  const wanted = new Set([...ids].map(String));
  const nodes = elements.filter((el) => !isEdge(dataOf(el)));
  const out: string[] = [];
  const hit = (d: Data) => [d.id, d.canonical_id, d.address, d.entity_id].some((k) => typeof k === 'string' && wanted.has(k));
  for (const [box, members] of boxMembers(nodes)) {
    if (members.some((m) => hit(dataOf(m)))) out.push(box);
  }
  return out;
}

/**
 * Fold every group box that is not in `expanded` into ONE card: the box keeps its id, name, member
 * count and per-token total (the header decorateCaseElements wrote), loses its children, and takes
 * the dominant placement role of its members so it stays in their column. Edges to or from the
 * members are re-pointed to the card and merged per (source, target, edge type): the label states
 * the transfer count and per-token amounts, never a cross-token sum (R15); edges inside one box
 * are dropped. Pure; input untouched. Boxes with no member nodes are left as they are.
 */
export function collapseGroupBoxes(
  elements: ElementDefinition[],
  expanded: ReadonlySet<string> = new Set(),
  pinned: ReadonlySet<string> = new Set(),
): ElementDefinition[] {
  const nodes = elements.filter((el) => !isEdge(dataOf(el)));
  const edges = elements.filter((el) => isEdge(dataOf(el)));
  const direct = boxMembers(nodes);
  const fold = new Map<string, ElementDefinition[]>();   // box id -> direct members
  // A group with more than BIG_GROUP_MIN members stays one card whatever `expanded` says; the officer
  // opens its member list instead and may pin single members onto the canvas (`pinned`).
  for (const [box, members] of direct) if (members.length > BIG_GROUP_MIN || !expanded.has(box)) fold.set(box, members);
  if (!fold.size) return elements;

  // Every descendant of a folded box maps to that box (members may themselves be parents).
  const owner = new Map<string, string>();
  const byParent = new Map<string, string[]>();
  for (const el of nodes) {
    const p = dataOf(el).parent;
    if (typeof p === 'string') byParent.set(p, [...(byParent.get(p) || []), String(dataOf(el).id)]);
  }
  const claim = (id: string, box: string) => {
    owner.set(id, box);
    for (const child of byParent.get(id) || []) claim(child, box);
  };
  const pinnedParent = new Map<string, string | undefined>();   // pinned member -> the lane its box sits in
  for (const [box, members] of fold) {
    const boxParent = str(dataOf(nodes.find((n) => dataOf(n).id === box) as ElementDefinition).parent);
    for (const m of members) {
      const mid = String(dataOf(m).id);
      if (pinned.has(mid) && members.length > BIG_GROUP_MIN) pinnedParent.set(mid, boxParent);
      else claim(mid, box);
    }
  }

  const outNodes: ElementDefinition[] = [];
  for (const el of nodes) {
    const d = dataOf(el);
    const id = String(d.id);
    if (pinnedParent.has(id)) {
      const { parent: _drop, ...rest } = d;
      const lane = pinnedParent.get(id);
      outNodes.push({ ...el, data: { ...rest, ...(lane ? { parent: lane } : {}), pinned_from_group: true } });
      continue;
    }
    if (owner.has(id)) continue;
    const members = fold.get(id);
    if (!members) { outNodes.push(el); continue; }
    const memberData = members.map(dataOf);
    const role = dominantRole(memberData);
    const risk = Math.max(0, ...memberData.map((m) => Number(m.risk_score) || 0));
    const big = members.length > BIG_GROUP_MIN;
    const origin: Partial<Record<OriginClass, number>> = {};
    if (big) for (const m of memberData) { const c = originClassOf(m); origin[c] = (origin[c] || 0) + 1; }
    const onCanvas = big ? members.filter((m) => pinnedParent.has(String(dataOf(m).id))).length : 0;
    const label = big
      ? [str(d.label) || id, originLine(origin), onCanvas ? `${onCanvas} on the canvas` : ''].filter(Boolean).join('\n')
      : d.label;
    outNodes.push({
      data: {
        ...d, collapsed: true, type: 'group_box',
        ...(label !== undefined ? { label } : {}),
        member_count: Array.isArray(d.member_ids) ? (d.member_ids as unknown[]).length : memberData.length,
        ...(role ? { placement_role: role } : {}),
        ...(big ? { big_group: true, origin_counts: origin } : {}),
        risk_score: risk,
      },
    });
  }

  const aggs = new Map<string, EdgeAgg>();
  const keptEdges: ElementDefinition[] = [];
  for (const el of edges) {
    const d = dataOf(el);
    const s = String(d.source);
    const t = String(d.target);
    const ns = owner.get(s) ?? s;
    const nt = owner.get(t) ?? t;
    if (ns === s && nt === t) { keptEdges.push(el); continue; }
    if (ns === nt) continue;   // inside one folded box
    const type = str(d.edge_type) || 'trace';
    const key = `${ns}>${nt}|${type}`;
    let a = aggs.get(key);
    if (!a) { a = { source: ns, target: nt, type, ids: [], transfers: 0, amounts: {}, anyLive: false, allStale: true }; aggs.set(key, a); }
    a.ids.push(String(d.id));
    a.transfers += Math.max(1, Number(d.transfer_count) || 1);
    for (const [tok, v] of Object.entries(edgeAmounts(d))) a.amounts[tok] = (a.amounts[tok] || 0) + v;
    if (d.determinism !== 'scored') a.anyLive = true;
    if (d.stale !== true) a.allStale = false;
  }
  const merged: ElementDefinition[] = [...aggs.values()].map((a) => {
    const money = formatAmountsByToken(a.amounts);
    const n = `${a.transfers} transfer${a.transfers === 1 ? '' : 's'}`;
    return {
      data: {
        id: `agg:${a.source}>${a.target}|${a.type}`, source: a.source, target: a.target, edge_type: a.type,
        determinism: a.anyLive ? 'deterministic' : 'scored', value: 0, step: 1,
        _edgeLabel: money ? `${n} · ${money}` : n,
        transfer_count: a.transfers, collapsed_edge_count: a.ids.length, merged_edge_ids: a.ids.slice(0, 50),
        ...(Object.keys(a.amounts).length ? { amounts_by_token: a.amounts } : {}),
        ...(a.allStale ? { stale: true, _visibility: VISIBILITY.EARLIER_RUN } : {}),
      },
    };
  });
  return [...outNodes, ...keptEdges, ...merged];
}
